import { useCallback, useEffect, useRef, useState } from "react";
import useHistoryManager from "./hooks/useHistoryManager";
import { EditingToolbar, ToolbarGroup } from "./StyledComponents";
import { Box, Button, Chip, IconButton, MenuItem, Popover, Select, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { Add, ContentCopy, ContentCut, ContentPaste, FormatAlignCenter, FormatAlignJustify, FormatAlignLeft, FormatAlignRight, FormatBold, FormatClear, FormatColorFill, FormatColorText, FormatItalic, FormatListBulleted, FormatListNumbered, FormatSize, FormatStrikethrough, FormatUnderlined, Image, Link, Redo, Remove, TableChart, Undo } from "@mui/icons-material";
import { InPlaceEditableEmailPreviewProps } from "./types";

const HistoryState = {
    html: '',
    timestamp: Date.now(),
  };

const InPlaceEditableEmailPreviewMAIQ: React.FC<InPlaceEditableEmailPreviewProps> = ({
    htmlContent,
    onHtmlChange,
    isEditable = false,
  }) => {
    const [isEditing, setIsEditing] = useState(false);
  const [isIframeInitialized, setIsIframeInitialized] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const editingElementRef = useRef<HTMLElement | null>(null);
    
    
    // Enhanced editing states
    const [fontSize, setFontSize] = useState(14);
    const [fontFamily, setFontFamily] = useState('Arial');
    const [textColor, setTextColor] = useState('#000000');
    const [backgroundColor, setBackgroundColor] = useState('#ffffff');
    const [alignment, setAlignment] = useState('left');
    const [formatting, setFormatting] = useState({
      bold: false,
      italic: false,
      underline: false,
      strikethrough: false,
    });
    
    // Color picker states
    const [showTextColorPicker, setShowTextColorPicker] = useState(false);
    const [showBgColorPicker, setShowBgColorPicker] = useState(false);
    const [colorPickerAnchor, setColorPickerAnchor] = useState<HTMLElement | null>(null);
    
    // History management
    const historyManager = useHistoryManager(htmlContent);
    
    
    // Global selection management
    const [lastSelection, setLastSelection] = useState<{
      range: Range | null;
      element: HTMLElement | null;
    }>({ range: null, element: null });
  
    // Save current selection
    const saveSelection = useCallback(() => {
      if (isEditable) {
        const iframe = containerRef.current?.querySelector('iframe') as HTMLIFrameElement;
        if (iframe && iframe.contentDocument) {
          const selection = iframe.contentDocument.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const element = range.commonAncestorContainer.parentElement as HTMLElement;
        setLastSelection({ range: range.cloneRange(), element });
      }
        }
      }
    }, [isEditable]);
  
    // Restore saved selection
    const restoreSelection = useCallback(() => {
      if (lastSelection.range && lastSelection.element && isEditable) {
        const iframe = containerRef.current?.querySelector('iframe') as HTMLIFrameElement;
        if (iframe && iframe.contentDocument) {
          const selection = iframe.contentDocument.getSelection();
        if (selection) {
          try {
            selection.removeAllRanges();
            selection.addRange(lastSelection.range);
            lastSelection.element.focus();
          } catch (error) {
            // If range is invalid, try to select the entire element
              const range = iframe.contentDocument.createRange();
            range.selectNodeContents(lastSelection.element);
            selection.removeAllRanges();
            selection.addRange(range);
            lastSelection.element.focus();
          }
        }
      }
      }
    }, [lastSelection, isEditable]);
  
    // Track selection changes
    const handleSelectionChange = useCallback(() => {
      if (isEditable) {
        const iframe = containerRef.current?.querySelector('iframe') as HTMLIFrameElement;
        if (iframe && iframe.contentDocument) {
          const selection = iframe.contentDocument.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const element = range.commonAncestorContainer.parentElement as HTMLElement;
        
            // Only save if the selection is within our editable iframe
            if (iframe.contentDocument.body.contains(element)) {
          setLastSelection({ range: range.cloneRange(), element });
        }
      }
        }
      }
    }, [isEditable]);

    // Handle input changes (direct text editing)
    const handleInputChange = useCallback((event: Event) => {
      const target = event.target as HTMLElement;
      
      // Only handle changes within our editable container and when in editing mode
      if (containerRef.current && containerRef.current.contains(target) && isEditable) {
        const editableContainer = containerRef.current.querySelector('#editable-html-container') as HTMLElement;
        if (editableContainer && onHtmlChange != null) {
          // Small delay to ensure DOM is updated
          setTimeout(() => {
            onHtmlChange(editableContainer.innerHTML);
          }, 10);
        }
      }
    }, [onHtmlChange, isEditable]);
  
    // Enhanced selection management for toolbar buttons
    const handleToolbarButtonClick = useCallback((callback: () => void) => {
      // First, try to restore the last saved selection
      restoreSelection();
      
      // Execute the callback
      callback();
      
      // Save the new selection after a short delay
      setTimeout(() => {
        saveSelection();
      }, 10);
    }, [restoreSelection, saveSelection]);
  
    // Enhanced function to find the best editable element
    const findEditableElement = useCallback((target: HTMLElement): HTMLElement | null => {
      // If the target itself is a good candidate, use it
      if (isGoodEditableElement(target)) {
        return target;
      }
  
      // Look for the best child element
      const candidates = target.querySelectorAll('p, div, span, h1, h2, h3, h4, h5, h6, td, th, li, strong, em, b, i');
      
      for (let i = 0; i < candidates.length; i++) {
        const element = candidates[i] as HTMLElement;
        if (isGoodEditableElement(element)) {
          return element;
        }
      }
  
      // If no good child found, check if we can make the target editable
      if (target.textContent?.trim() && !target.querySelector('img, video, iframe')) {
        return target;
      }
  
      return null;
    }, []);
  
    // Check if an element is good for editing
    const isGoodEditableElement = useCallback((element: HTMLElement): boolean => {
      // Skip interactive elements
      if (['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA'].includes(element.tagName)) {
        return false;
      }
  
      // Skip elements with media
      if (element.querySelector('img, video, iframe')) {
        return false;
      }
  
      // Must have text content
      if (!element.textContent?.trim()) {
        return false;
      }
  
      // Prefer elements with direct text content (no nested text elements)
      const hasNestedTextElements = element.querySelector('p, div, span, h1, h2, h3, h4, h5, h6');
      if (hasNestedTextElements && element.children.length > 0) {
        return false;
      }
  
      return true;
    }, []);
  
  
    // Make entire content editable on modal open
    const makeContentEditable = useCallback(() => {
      if (containerRef.current && isEditable) {
          // Set editing state
          setIsEditing(true);
          
        // Wait for iframe to load and then add initial state to history
          setTimeout(() => {
          const iframe = containerRef.current?.querySelector('iframe') as HTMLIFrameElement;
          if (iframe && iframe.contentDocument) {
            const editableContainer = iframe.contentDocument.body;
            if (editableContainer) {
              editingElementRef.current = editableContainer;
              iframe.contentWindow?.focus();
              editableContainer.focus();
            historyManager.addToHistory(editableContainer.innerHTML);
        }
      }
        }, 100);
      }
    }, [historyManager, isEditable]);
  
    // Handle click events for better UX
    const handleClick = useCallback(
      (event: React.MouseEvent) => {
        // Prevent event bubbling to maintain selection
        event.stopPropagation();
        
        // Just ensure focus is maintained
        if (isEditable) {
          const iframe = containerRef.current?.querySelector('iframe') as HTMLIFrameElement;
          if (iframe && iframe.contentDocument) {
            iframe.contentWindow?.focus();
            iframe.contentDocument.body.focus();
          }
        }
      },
      [isEditable],
    );
  
    // Handle toolbar button clicks to prevent selection loss
    const handleToolbarClick = useCallback((event: React.MouseEvent) => {
      // Prevent the click from bubbling up and losing selection
      event.preventDefault();
      event.stopPropagation();
      
      // Save current selection before any toolbar action
      saveSelection();
    }, [saveSelection]);
  
    // Enhanced editing functions with proper selection handling and history tracking
    const executeCommand = useCallback((command: string, value?: string) => {
      // Only execute commands when in editing mode
      if (!isEditable) return false;
      
      // Get the iframe and its document
      const iframe = containerRef.current?.querySelector('iframe') as HTMLIFrameElement;
      if (!iframe || !iframe.contentDocument) return false;
      
      const iframeDoc = iframe.contentDocument;
      const editableContainer = iframeDoc.body;
      if (!editableContainer) return false;
  
      // Save current scroll position from body (which now handles scrolling)
      const scrollTop = editableContainer.scrollTop;
      const scrollLeft = editableContainer.scrollLeft;
      
      // Focus the iframe content
      iframe.contentWindow?.focus();
      editableContainer.focus();
      
      // Get current selection within iframe
      const selection = iframeDoc.getSelection();
      let hasSelection = selection && selection.rangeCount > 0;
      
      // If no selection, create a selection at cursor position
      if (!hasSelection) {
        const range = iframeDoc.createRange();
        range.selectNodeContents(editableContainer);
        range.collapse(false); // Move to end
        selection?.removeAllRanges();
        selection?.addRange(range);
        hasSelection = true;
      }
      
      // Execute the command in iframe document
      const success = iframeDoc.execCommand(command, false, value);
      
      // Restore scroll position immediately
      editableContainer.scrollTop = scrollTop;
      editableContainer.scrollLeft = scrollLeft;
      
      // Update HTML content and history after command execution
      setTimeout(() => {
        if (success) {
          const updatedHtml = editableContainer.innerHTML;
          historyManager.addToHistory(updatedHtml);
          if (onHtmlChange) {
            onHtmlChange(updatedHtml);
          }
        }
        
        // Ensure scroll position is maintained after updates
        editableContainer.scrollTop = scrollTop;
        editableContainer.scrollLeft = scrollLeft;
      }, 10);
      
      // Keep focus on iframe
      iframe.contentWindow?.focus();
      editableContainer.focus();
      
      return success;
    }, [isEditable, historyManager, onHtmlChange]);
  
    const applyFormatting = useCallback((format: string) => {
      // Execute the command directly (history is already tracked in executeCommand)
      executeCommand(format);
      
      // Update formatting state
      setFormatting(prev => ({
        ...prev,
        [format]: !prev[format as keyof typeof prev]
      }));
    }, [executeCommand]);
  
    const insertLink = useCallback(() => {
      const url = prompt('Enter URL:');
      if (url) {
        executeCommand('createLink', url);
      }
    }, [executeCommand]);
  
    const insertImage = useCallback(() => {
      const url = prompt('Enter image URL:');
      if (url) {
        executeCommand('insertImage', url);
      }
    }, [executeCommand]);
  
    const insertTable = useCallback(() => {
      if (!isEditable) return;
      
      const rows = prompt('Number of rows:', '3');
      const cols = prompt('Number of columns:', '3');
      if (rows && cols) {
        const iframe = containerRef.current?.querySelector('iframe') as HTMLIFrameElement;
        if (iframe && iframe.contentDocument) {
          const editableContainer = iframe.contentDocument.body;
          
          // Save current scroll position
          const scrollTop = editableContainer.scrollTop;
          const scrollLeft = editableContainer.scrollLeft;
          
          const table = iframe.contentDocument.createElement('table');
        table.style.border = '1px solid #ccc';
        table.style.borderCollapse = 'collapse';
        table.style.width = '100%';
        table.style.margin = '10px 0';
        
        for (let i = 0; i < parseInt(rows); i++) {
            const row = iframe.contentDocument.createElement('tr');
          for (let j = 0; j < parseInt(cols); j++) {
              const cell = iframe.contentDocument.createElement('td');
            cell.style.border = '1px solid #ccc';
            cell.style.padding = '8px';
            cell.style.minWidth = '50px';
            cell.innerHTML = '&nbsp;';
            row.appendChild(cell);
          }
          table.appendChild(row);
        }
        
          editableContainer.appendChild(table);
          
          // Restore scroll position
          editableContainer.scrollTop = scrollTop;
          editableContainer.scrollLeft = scrollLeft;
          
          iframe.contentWindow?.focus();
          editableContainer.focus();
          // Add to history
          const updatedHtml = editableContainer.innerHTML;
          historyManager.addToHistory(updatedHtml);
          // Update HTML content
          if (onHtmlChange) {
            onHtmlChange(updatedHtml);
          }
        }
      }
    }, [historyManager, onHtmlChange, isEditable]);
  
    // Copy/Paste functionality
    const copyContent = useCallback(() => {
      if (isEditable) {
        const iframe = containerRef.current?.querySelector('iframe') as HTMLIFrameElement;
        if (iframe && iframe.contentDocument) {
          const selection = iframe.contentDocument.getSelection();
        if (selection && selection.toString()) {
          navigator.clipboard.writeText(selection.toString());
        } else {
            navigator.clipboard.writeText(iframe.contentDocument.body.textContent || '');
        }
      }
      }
    }, [isEditable]);
  
    const pasteContent = useCallback(async () => {
      if (isEditable) {
        const iframe = containerRef.current?.querySelector('iframe') as HTMLIFrameElement;
        if (iframe && iframe.contentDocument) {
          const editableContainer = iframe.contentDocument.body;
        try {
            // Save current scroll position
            const scrollTop = editableContainer.scrollTop;
            const scrollLeft = editableContainer.scrollLeft;
            
          const text = await navigator.clipboard.readText();
            iframe.contentDocument.execCommand('insertText', false, text);
            
            // Restore scroll position
            editableContainer.scrollTop = scrollTop;
            editableContainer.scrollLeft = scrollLeft;
            
            iframe.contentWindow?.focus();
            editableContainer.focus();
          // Add to history after paste
            const updatedHtml = editableContainer.innerHTML;
            historyManager.addToHistory(updatedHtml);
          // Update HTML content
          if (onHtmlChange) {
              onHtmlChange(updatedHtml);
          }
        } catch (err) {
          console.error('Failed to paste content:', err);
        }
      }
      }
    }, [historyManager, onHtmlChange, isEditable]);
  
    const cutContent = useCallback(() => {
      if (isEditable) {
        const iframe = containerRef.current?.querySelector('iframe') as HTMLIFrameElement;
        if (iframe && iframe.contentDocument) {
          const editableContainer = iframe.contentDocument.body;
          const selection = iframe.contentDocument.getSelection();
        if (selection && selection.toString()) {
            // Save current scroll position
            const scrollTop = editableContainer.scrollTop;
            const scrollLeft = editableContainer.scrollLeft;
            
          navigator.clipboard.writeText(selection.toString());
            iframe.contentDocument.execCommand('delete');
            
            // Restore scroll position
            editableContainer.scrollTop = scrollTop;
            editableContainer.scrollLeft = scrollLeft;
            
          // Add to history after cut
            const updatedHtml = editableContainer.innerHTML;
            historyManager.addToHistory(updatedHtml);
          // Update HTML content
          if (onHtmlChange) {
              onHtmlChange(updatedHtml);
          }
        }
      }
      }
    }, [historyManager, onHtmlChange, isEditable]);
  
    const clearFormatting = useCallback(() => {
      executeCommand('removeFormat');
    }, [executeCommand]);
  
    // Color change functions
    const handleTextColorChange = useCallback((color: string) => {
      setTextColor(color);
      if (isEditable) {
        const iframe = containerRef.current?.querySelector('iframe') as HTMLIFrameElement;
        if (iframe && iframe.contentDocument) {
          const editableContainer = iframe.contentDocument.body;
          
          // Save current scroll position
          const scrollTop = editableContainer.scrollTop;
          const scrollLeft = editableContainer.scrollLeft;
          
          // Focus iframe first
          iframe.contentWindow?.focus();
          editableContainer.focus();
          
          // Get selection
          const selection = iframe.contentDocument.getSelection();
          if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            
            if (range.collapsed) {
              // If no text selected, apply to current typing
              const span = iframe.contentDocument.createElement('span');
              span.style.color = color;
              span.innerHTML = '&nbsp;'; // Add non-breaking space
              range.insertNode(span);
              
              // Move cursor inside the span
              const newRange = iframe.contentDocument.createRange();
              newRange.setStart(span.firstChild!, 1);
              newRange.setEnd(span.firstChild!, 1);
              selection.removeAllRanges();
              selection.addRange(newRange);
            } else {
              // If text is selected, wrap it in span
              const selectedContent = range.extractContents();
              const span = iframe.contentDocument.createElement('span');
              span.style.color = color;
              span.appendChild(selectedContent);
              range.insertNode(span);
              
              // Select the span content
              const newRange = iframe.contentDocument.createRange();
              newRange.selectNodeContents(span);
              selection.removeAllRanges();
              selection.addRange(newRange);
            }
            
            // Restore scroll position
            editableContainer.scrollTop = scrollTop;
            editableContainer.scrollLeft = scrollLeft;
            
            // Add to history
            historyManager.addToHistory(editableContainer.innerHTML);
            // Update HTML content
            if (onHtmlChange) {
              onHtmlChange(editableContainer.innerHTML);
            }
          }
        }
      }
      setShowTextColorPicker(false);
    }, [historyManager, onHtmlChange, isEditable]);
  
    const handleBackgroundColorChange = useCallback((color: string) => {
      setBackgroundColor(color);
      if (isEditable) {
        const iframe = containerRef.current?.querySelector('iframe') as HTMLIFrameElement;
        if (iframe && iframe.contentDocument) {
          const editableContainer = iframe.contentDocument.body;
          
          // Save current scroll position
          const scrollTop = editableContainer.scrollTop;
          const scrollLeft = editableContainer.scrollLeft;
          
          // Focus iframe first
          iframe.contentWindow?.focus();
          editableContainer.focus();
          
          // Get selection
          const selection = iframe.contentDocument.getSelection();
          if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            
            if (range.collapsed) {
              // If no text selected, apply to current typing
              const span = iframe.contentDocument.createElement('span');
              span.style.backgroundColor = color;
              span.innerHTML = '&nbsp;'; // Add non-breaking space
              range.insertNode(span);
              
              // Move cursor inside the span
              const newRange = iframe.contentDocument.createRange();
              newRange.setStart(span.firstChild!, 1);
              newRange.setEnd(span.firstChild!, 1);
              selection.removeAllRanges();
              selection.addRange(newRange);
            } else {
              // If text is selected, wrap it in span
              const selectedContent = range.extractContents();
              const span = iframe.contentDocument.createElement('span');
              span.style.backgroundColor = color;
              span.appendChild(selectedContent);
              range.insertNode(span);
              
              // Select the span content
              const newRange = iframe.contentDocument.createRange();
              newRange.selectNodeContents(span);
              selection.removeAllRanges();
              selection.addRange(newRange);
            }
            
            // Restore scroll position
            editableContainer.scrollTop = scrollTop;
            editableContainer.scrollLeft = scrollLeft;
            
            // Add to history
            historyManager.addToHistory(editableContainer.innerHTML);
            // Update HTML content
            if (onHtmlChange) {
              onHtmlChange(editableContainer.innerHTML);
            }
          }
        }
      }
      setShowBgColorPicker(false);
    }, [historyManager, onHtmlChange, isEditable]);
  
    const openTextColorPicker = useCallback((event: React.MouseEvent<HTMLElement>) => {
      setColorPickerAnchor(event.currentTarget);
      setShowTextColorPicker(true);
      setShowBgColorPicker(false);
    }, []);
  
    const openBgColorPicker = useCallback((event: React.MouseEvent<HTMLElement>) => {
      setColorPickerAnchor(event.currentTarget);
      setShowBgColorPicker(true);
      setShowTextColorPicker(false);
    }, []);
  
    const closeColorPickers = useCallback(() => {
      setShowTextColorPicker(false);
      setShowBgColorPicker(false);
      setColorPickerAnchor(null);
    }, []);

    // Font change functions
    const handleFontFamilyChange = useCallback((font: string) => {
      setFontFamily(font);
      if (isEditable) {
        const iframe = containerRef.current?.querySelector('iframe') as HTMLIFrameElement;
        if (iframe && iframe.contentDocument) {
          const editableContainer = iframe.contentDocument.body;
          
          // Save current scroll position
          const scrollTop = editableContainer.scrollTop;
          const scrollLeft = editableContainer.scrollLeft;
          
          // Focus iframe first
          iframe.contentWindow?.focus();
          editableContainer.focus();
          
          // Get selection
          const selection = iframe.contentDocument.getSelection();
          if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            
            if (range.collapsed) {
              // If no text selected, apply to current typing
              const span = iframe.contentDocument.createElement('span');
              span.style.fontFamily = font;
              span.innerHTML = '&nbsp;'; // Add non-breaking space
              range.insertNode(span);
              
              // Move cursor inside the span
              const newRange = iframe.contentDocument.createRange();
              newRange.setStart(span.firstChild!, 1);
              newRange.setEnd(span.firstChild!, 1);
              selection.removeAllRanges();
              selection.addRange(newRange);
            } else {
              // If text is selected, wrap it in span
              const selectedContent = range.extractContents();
              const span = iframe.contentDocument.createElement('span');
              span.style.fontFamily = font;
              span.appendChild(selectedContent);
              range.insertNode(span);
              
              // Select the span content
              const newRange = iframe.contentDocument.createRange();
              newRange.selectNodeContents(span);
              selection.removeAllRanges();
              selection.addRange(newRange);
            }
            
            // Restore scroll position
            editableContainer.scrollTop = scrollTop;
            editableContainer.scrollLeft = scrollLeft;
            
            // Add to history
            historyManager.addToHistory(editableContainer.innerHTML);
            // Update HTML content
            if (onHtmlChange) {
              onHtmlChange(editableContainer.innerHTML);
            }
          }
        }
      }
    }, [historyManager, onHtmlChange, isEditable]);

    const handleFontSizeChange = useCallback((size: number) => {
      setFontSize(size);
      if (isEditable) {
        const iframe = containerRef.current?.querySelector('iframe') as HTMLIFrameElement;
        if (iframe && iframe.contentDocument) {
          const editableContainer = iframe.contentDocument.body;
          
          // Save current scroll position
          const scrollTop = editableContainer.scrollTop;
          const scrollLeft = editableContainer.scrollLeft;
          
          // Focus iframe first
          iframe.contentWindow?.focus();
          editableContainer.focus();
          
          // Get selection
          const selection = iframe.contentDocument.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
            
            if (range.collapsed) {
              // If no text selected, apply to current typing
              const span = iframe.contentDocument.createElement('span');
          span.style.fontSize = `${size}px`;
              span.innerHTML = '&nbsp;'; // Add non-breaking space
            range.insertNode(span);
              
              // Move cursor inside the span
              const newRange = iframe.contentDocument.createRange();
              newRange.setStart(span.firstChild!, 1);
              newRange.setEnd(span.firstChild!, 1);
              selection.removeAllRanges();
              selection.addRange(newRange);
            } else {
              // If text is selected, wrap it in span
              const selectedContent = range.extractContents();
              const span = iframe.contentDocument.createElement('span');
              span.style.fontSize = `${size}px`;
              span.appendChild(selectedContent);
              range.insertNode(span);
              
              // Select the span content
              const newRange = iframe.contentDocument.createRange();
              newRange.selectNodeContents(span);
              selection.removeAllRanges();
              selection.addRange(newRange);
            }
            
            // Restore scroll position
            editableContainer.scrollTop = scrollTop;
            editableContainer.scrollLeft = scrollLeft;
            
          // Add to history
          historyManager.addToHistory(editableContainer.innerHTML);
          // Update HTML content
          if (onHtmlChange) {
            onHtmlChange(editableContainer.innerHTML);
          }
        }
      }
      }
    }, [historyManager, onHtmlChange, isEditable]);

    const increaseFontSize = useCallback(() => {
      const newSize = Math.min(fontSize + 2, 72);
      handleFontSizeChange(newSize);
    }, [fontSize, handleFontSizeChange]);

    const decreaseFontSize = useCallback(() => {
      const newSize = Math.max(fontSize - 2, 8);
      handleFontSizeChange(newSize);
    }, [fontSize, handleFontSizeChange]);
  
  
    const handleUndo = useCallback(() => {
      if (historyManager.canUndo && isEditable) {
        const previousState = historyManager.undo();
        if (containerRef.current) {
          const iframe = containerRef.current.querySelector('iframe') as HTMLIFrameElement;
          if (iframe && iframe.contentDocument) {
            const editableContainer = iframe.contentDocument.body;
          if (editableContainer) {
              // Save current scroll position
              const scrollTop = editableContainer.scrollTop;
              const scrollLeft = editableContainer.scrollLeft;
              
            editableContainer.innerHTML = previousState;
              
              // Restore scroll position
              editableContainer.scrollTop = scrollTop;
              editableContainer.scrollLeft = scrollLeft;
              
              iframe.contentWindow?.focus();
            editableContainer.focus();
            if (onHtmlChange) {
              onHtmlChange(previousState);
            }
          }
        }
      }
      }
    }, [historyManager, onHtmlChange, isEditable]);
  
    const handleRedo = useCallback(() => {
      if (historyManager.canRedo && isEditable) {
        const nextState = historyManager.redo();
        if (containerRef.current) {
          const iframe = containerRef.current.querySelector('iframe') as HTMLIFrameElement;
          if (iframe && iframe.contentDocument) {
            const editableContainer = iframe.contentDocument.body;
          if (editableContainer) {
              // Save current scroll position
              const scrollTop = editableContainer.scrollTop;
              const scrollLeft = editableContainer.scrollLeft;
              
            editableContainer.innerHTML = nextState;
              
              // Restore scroll position
              editableContainer.scrollTop = scrollTop;
              editableContainer.scrollLeft = scrollLeft;
              
              iframe.contentWindow?.focus();
            editableContainer.focus();
            if (onHtmlChange) {
              onHtmlChange(nextState);
            }
          }
        }
      }
      }
    }, [historyManager, onHtmlChange, isEditable]);
  
    // Save changes with history tracking
    const saveChanges = useCallback(() => {
      if (editingElementRef.current && containerRef.current) {
        const newHtml = editingElementRef.current.innerHTML;

        // Get the updated HTML from the container
        const htmlContainer = containerRef.current.querySelector(
          '#editable-html-container',
        );
        if (htmlContainer && onHtmlChange) {
          const updatedHtml = htmlContainer.innerHTML;
          onHtmlChange(updatedHtml);
          // Add to history
          historyManager.addToHistory(updatedHtml);
        }

        // Keep editing mode active - don't reset state
        // User can continue editing
      }
    }, [onHtmlChange, historyManager]);
  
    // Cancel editing - reset to original content
    const cancelEditing = useCallback(() => {
      if (editingElementRef.current) {
        // Restore original content and HTML
        editingElementRef.current.innerHTML = htmlContent;
        
        // Reset to original state
        if (onHtmlChange) {
          onHtmlChange(htmlContent);
        }
      }
    }, [htmlContent, onHtmlChange]);
  
    // Enhanced keyboard event handler with shortcuts
    const handleKeyDown = useCallback(
      (event: KeyboardEvent) => {
        if (isEditing) {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            saveChanges();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            cancelEditing();
          }
        }
        
        // Global shortcuts (work even when not in editing mode)
        if (event.ctrlKey || event.metaKey) {
          switch (event.key) {
            case 'z':
              event.preventDefault();
              if (event.shiftKey) {
                handleRedo();
              } else {
                handleUndo();
              }
              break;
            case 'y':
              event.preventDefault();
              handleRedo();
              break;
            case 'b':
              event.preventDefault();
              applyFormatting('bold');
              break;
            case 'i':
              event.preventDefault();
              applyFormatting('italic');
              break;
            case 'u':
              event.preventDefault();
              applyFormatting('underline');
              break;
            case 's':
              event.preventDefault();
              saveChanges();
              break;
            case 'c':
              if (!event.shiftKey) {
                event.preventDefault();
                copyContent();
              }
              break;
            case 'v':
              event.preventDefault();
              pasteContent();
              break;
            case 'x':
              event.preventDefault();
              cutContent();
              break;
            case '=':
            case '+':
              if (event.shiftKey) {
                event.preventDefault();
                increaseFontSize();
              }
              break;
            case '-':
              event.preventDefault();
              decreaseFontSize();
              break;
          }
        }
      },
      [isEditing, saveChanges, cancelEditing, handleUndo, handleRedo, applyFormatting, copyContent, pasteContent, cutContent, increaseFontSize, decreaseFontSize],
    );
  
    // Handle outside clicks - just save selection, don't exit editing mode
    const handleDocumentClick = useCallback(
      (event: Event) => {
        if (
          isEditing &&
          editingElementRef.current &&
          event.target instanceof Node &&
          !editingElementRef.current.contains(event.target as Node)
        ) {
          // Save the current selection
          saveSelection();
        }
      },
      [isEditing, saveSelection],
    );
  
    // Add event listeners
    useEffect(() => {
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('click', handleDocumentClick);
      document.addEventListener('selectionchange', handleSelectionChange);

      // Add message listener for iframe communication with optimized handling
      let historyTimeout: NodeJS.Timeout;
      let lastContent = htmlContent;
      
      const handleMessage = (event: MessageEvent) => {
        if (event.data.type === 'htmlChange' && onHtmlChange) {
          const newContent = event.data.html;
          
          // Only update if content actually changed
          if (newContent !== lastContent) {
            lastContent = newContent;
            onHtmlChange(newContent);
            
            // Debounce history updates to prevent excessive history entries
            clearTimeout(historyTimeout);
            historyTimeout = setTimeout(() => {
              historyManager.addToHistory(newContent);
            }, 1000); // 1 second debounce for history
          }
        }
      };
      
      window.addEventListener('message', handleMessage);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.removeEventListener('click', handleDocumentClick);
        document.removeEventListener('selectionchange', handleSelectionChange);
        window.removeEventListener('message', handleMessage);
      };
    }, [handleKeyDown, handleDocumentClick, handleSelectionChange, onHtmlChange, historyManager]);

    // Add input event listeners to capture direct text editing
    useEffect(() => {
      const container = containerRef.current;
      if (container && isEditable) {
        container.addEventListener('input', handleInputChange);
        container.addEventListener('paste', handleInputChange);
        container.addEventListener('cut', handleInputChange);
        
        return () => {
          container.removeEventListener('input', handleInputChange);
          container.removeEventListener('paste', handleInputChange);
          container.removeEventListener('cut', handleInputChange);
        };
      }
    }, [isEditable, handleInputChange]);
  
    // Reset iframe initialization when isEditable changes
    useEffect(() => {
      setIsIframeInitialized(false);
    }, [isEditable]);
  
    // Make content editable when isEditable is true
    useEffect(() => {
      if (isEditable) {
        // Small delay to ensure DOM is ready
        const timer = setTimeout(() => {
          makeContentEditable();
        }, 100);
        
        return () => clearTimeout(timer);
      }
    }, [isEditable, makeContentEditable]);
  
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%',
        overflow: 'hidden' // Prevent outer scroll
      }}>
        {/* Enhanced Editing Toolbar */}
        <EditingToolbar>
          {/* History Controls */}
          <ToolbarGroup>
            <Tooltip title="Undo (Ctrl+Z)">
              <IconButton
                size="small"
                onClick={(e) => {
                  handleToolbarClick(e);
                  handleUndo();
                }}
                disabled={!historyManager.canUndo}
                sx={{ 
                  opacity: historyManager.canUndo ? 1 : 0.5,
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                }}
              >
                <Undo fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Redo (Ctrl+Y)">
              <IconButton
                size="small"
                onClick={(e) => {
                  handleToolbarClick(e);
                  handleRedo();
                }}
                disabled={!historyManager.canRedo}
                sx={{ 
                  opacity: historyManager.canRedo ? 1 : 0.5,
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                }}
              >
                <Redo fontSize="small" />
              </IconButton>
            </Tooltip>
          </ToolbarGroup>
  
          {/* Text Formatting */}
          <ToolbarGroup>
            <Tooltip title="Bold (Ctrl+B)">
              <ToggleButton
                size="small"
                value="bold"
                selected={formatting.bold}
                onChange={(e) => {
                  e.preventDefault();
                  handleToolbarClick(e);
                  applyFormatting('bold');
                }}
                sx={{ minWidth: '32px', height: '32px' }}
              >
                <FormatBold fontSize="small" />
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Italic (Ctrl+I)">
              <ToggleButton
                size="small"
                value="italic"
                selected={formatting.italic}
                onChange={(e) => {
                  e.preventDefault();
                  handleToolbarClick(e);
                  applyFormatting('italic');
                }}
                sx={{ minWidth: '32px', height: '32px' }}
              >
                <FormatItalic fontSize="small" />
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Underline (Ctrl+U)">
              <ToggleButton
                size="small"
                value="underline"
                selected={formatting.underline}
                onChange={(e) => {
                  e.preventDefault();
                  handleToolbarClick(e);
                  applyFormatting('underline');
                }}
                sx={{ minWidth: '32px', height: '32px' }}
              >
                <FormatUnderlined fontSize="small" />
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Strikethrough">
              <ToggleButton
                size="small"
                value="strikethrough"
                selected={formatting.strikethrough}
                onChange={(e) => {
                  e.preventDefault();
                  handleToolbarClick(e);
                  applyFormatting('strikethrough');
                }}
                sx={{ minWidth: '32px', height: '32px' }}
              >
                <FormatStrikethrough fontSize="small" />
              </ToggleButton>
            </Tooltip>
          </ToolbarGroup>

          {/* Font Controls */}
          <ToolbarGroup>
            <Tooltip title="Font Family">
              <Select
                size="small"
                value={fontFamily}
                onChange={(e) => {
                  handleToolbarClick(e as any);
                  handleFontFamilyChange(e.target.value as string);
                }}
                sx={{ 
                  minWidth: '120px', 
                  height: '32px',
                  '& .MuiSelect-select': {
                    padding: '4px 8px',
                    fontSize: '12px'
                  }
                }}
              >
                <MenuItem value="Arial">Arial</MenuItem>
                <MenuItem value="Helvetica">Helvetica</MenuItem>
                <MenuItem value="Times New Roman">Times New Roman</MenuItem>
                <MenuItem value="Georgia">Georgia</MenuItem>
                <MenuItem value="Verdana">Verdana</MenuItem>
                <MenuItem value="Courier New">Courier New</MenuItem>
                <MenuItem value="Trebuchet MS">Trebuchet MS</MenuItem>
                <MenuItem value="Impact">Impact</MenuItem>
                <MenuItem value="Comic Sans MS">Comic Sans MS</MenuItem>
                <MenuItem value="Tahoma">Tahoma</MenuItem>
                <MenuItem value="Calibri">Calibri</MenuItem>
                <MenuItem value="Palatino">Palatino</MenuItem>
              </Select>
            </Tooltip>
            <Tooltip title="Font Size">
              <Select
                size="small"
                value={fontSize}
                onChange={(e) => {
                  handleToolbarClick(e as any);
                  handleFontSizeChange(Number(e.target.value));
                }}
                sx={{ 
                  minWidth: '80px', 
                  height: '32px',
                  '& .MuiSelect-select': {
                    padding: '4px 8px',
                    fontSize: '12px'
                  }
                }}
              >
                <MenuItem value={8}>8px</MenuItem>
                <MenuItem value={9}>9px</MenuItem>
                <MenuItem value={10}>10px</MenuItem>
                <MenuItem value={11}>11px</MenuItem>
                <MenuItem value={12}>12px</MenuItem>
                <MenuItem value={14}>14px</MenuItem>
                <MenuItem value={16}>16px</MenuItem>
                <MenuItem value={18}>18px</MenuItem>
                <MenuItem value={20}>20px</MenuItem>
                <MenuItem value={24}>24px</MenuItem>
                <MenuItem value={28}>28px</MenuItem>
                <MenuItem value={32}>32px</MenuItem>
                <MenuItem value={36}>36px</MenuItem>
                <MenuItem value={48}>48px</MenuItem>
                <MenuItem value={72}>72px</MenuItem>
              </Select>
            </Tooltip>
            <Tooltip title="Decrease Font Size">
              <IconButton
                size="small"
                onClick={(e) => {
                  handleToolbarClick(e);
                  decreaseFontSize();
                }}
                disabled={fontSize <= 8}
                sx={{ 
                  minWidth: '32px', 
                  height: '32px',
                  opacity: fontSize <= 8 ? 0.5 : 1,
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                }}
              >
                <Remove fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Increase Font Size">
              <IconButton
                size="small"
                onClick={(e) => {
                  handleToolbarClick(e);
                  increaseFontSize();
                }}
                disabled={fontSize >= 72}
                sx={{ 
                  minWidth: '32px', 
                  height: '32px',
                  opacity: fontSize >= 72 ? 0.5 : 1,
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                }}
              >
                <Add fontSize="small" />
              </IconButton>
            </Tooltip>
          </ToolbarGroup>
  
          {/* Lists */}
          <ToolbarGroup>
            <Tooltip title="Bulleted List">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  restoreSelection();
                  executeCommand('insertUnorderedList');
                  setTimeout(() => {
                    saveSelection();
                  }, 10);
                }}
                sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
              >
                <FormatListBulleted fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Numbered List">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  restoreSelection();
                  executeCommand('insertOrderedList');
                  setTimeout(() => {
                    saveSelection();
                  }, 10);
                }}
                sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
              >
                <FormatListNumbered fontSize="small" />
              </IconButton>
            </Tooltip>
          </ToolbarGroup>
  
          {/* Alignment */}
          <ToolbarGroup>
            <ToggleButtonGroup
              size="small"
              value={alignment}
              exclusive
              onChange={(_, newAlignment) => {
                if (newAlignment) {
                  setAlignment(newAlignment);
                  restoreSelection();
                  executeCommand('justify' + newAlignment.charAt(0).toUpperCase() + newAlignment.slice(1));
                  setTimeout(() => {
                    saveSelection();
                  }, 10);
                }
              }}
              sx={{ height: '32px' }}
            >
              <ToggleButton value="left" sx={{ minWidth: '32px' }}>
                <FormatAlignLeft fontSize="small" />
              </ToggleButton>
              <ToggleButton value="center" sx={{ minWidth: '32px' }}>
                <FormatAlignCenter fontSize="small" />
              </ToggleButton>
              <ToggleButton value="right" sx={{ minWidth: '32px' }}>
                <FormatAlignRight fontSize="small" />
              </ToggleButton>
              <ToggleButton value="justify" sx={{ minWidth: '32px' }}>
                <FormatAlignJustify fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </ToolbarGroup>
  
          {/* Copy/Paste Operations */}
          <ToolbarGroup>
            <Tooltip title="Copy (Ctrl+C)">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  handleToolbarClick(e);
                  copyContent();
                }}
                sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Paste (Ctrl+V)">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  handleToolbarClick(e);
                  pasteContent();
                }}
                sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
              >
                <ContentPaste fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Cut (Ctrl+X)">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  handleToolbarClick(e);
                  cutContent();
                }}
                sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
              >
                <ContentCut fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Clear Formatting">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  handleToolbarClick(e);
                  clearFormatting();
                }}
                sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
              >
                <FormatClear fontSize="small" />
              </IconButton>
            </Tooltip>
          </ToolbarGroup>
  
          {/* Insert Elements */}
          <ToolbarGroup>
            <Tooltip title="Insert Link">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  handleToolbarClick(e);
                  insertLink();
                }}
                sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
              >
                <Link fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Insert Image">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  handleToolbarClick(e);
                  insertImage();
                }}
                sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
              >
                <Image fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Insert Table">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  handleToolbarClick(e);
                  insertTable();
                }}
                sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
              >
                <TableChart fontSize="small" />
              </IconButton>
            </Tooltip>
          </ToolbarGroup>
  
          {/* Color Options */}
          <ToolbarGroup>
            <Tooltip title="Text Color">
              <IconButton
                size="small"
                onClick={(e) => {
                  handleToolbarClick(e);
                  openTextColorPicker(e);
                }}
                sx={{ 
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                  position: 'relative'
                }}
              >
                <FormatColorText fontSize="small" />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '2px',
                    right: '2px',
                    width: '8px',
                    height: '8px',
                    backgroundColor: textColor,
                    border: '1px solid #ccc',
                    borderRadius: '50%',
                  }}
                />
              </IconButton>
            </Tooltip>
            <Tooltip title="Background Color">
              <IconButton
                size="small"
                onClick={(e) => {
                  handleToolbarClick(e);
                  openBgColorPicker(e);
                }}
                sx={{ 
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                  position: 'relative'
                }}
              >
                <FormatColorFill fontSize="small" />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '2px',
                    right: '2px',
                    width: '8px',
                    height: '8px',
                    backgroundColor: backgroundColor,
                    border: '1px solid #ccc',
                    borderRadius: '50%',
                  }}
                />
              </IconButton>
            </Tooltip>
          </ToolbarGroup>
  
          {/* History Info */}
          <ToolbarGroup>
            <Chip
              label={`${historyManager.currentIndex + 1}/${historyManager.historyLength}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: '11px', height: '24px' }}
            />
          </ToolbarGroup>
        </EditingToolbar>
  
        {/* Color Picker Popover */}
        <Popover
          open={showTextColorPicker || showBgColorPicker}
          onClose={closeColorPickers}
          anchorEl={colorPickerAnchor}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <Box sx={{ p: 2, minWidth: '280px' }}>
            <Typography variant="subtitle2" gutterBottom>
              {showTextColorPicker ? 'Text Color' : 'Background Color'}
            </Typography>
            
            {/* Color Palette */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 1, mb: 2 }}>
              {[
                '#000000', '#333333', '#666666', '#999999', '#CCCCCC', '#FFFFFF', '#FF0000', '#FF6600',
                '#FFCC00', '#00FF00', '#00CCFF', '#0066FF', '#6600FF', '#FF00CC', '#FF6666', '#FFCC66',
                '#CCFF66', '#66FFCC', '#66CCFF', '#6666FF', '#CC66FF', '#FF66CC', '#FF9999', '#FFCC99',
                '#CCFF99', '#99FFCC', '#99CCFF', '#9999FF', '#CC99FF', '#FF99CC', '#FFB3B3', '#FFD9B3',
                '#D9FFB3', '#B3FFD9', '#B3D9FF', '#B3B3FF', '#D9B3FF', '#FFB3D9', '#FFCCCC', '#FFE6CC',
                '#E6FFCC', '#CCFFE6', '#CCE6FF', '#CCCCFF', '#E6CCFF', '#FFCCE6', '#F0F0F0', '#E0E0E0'
              ].map((color) => (
                <Box
                  key={color}
                  onClick={() => {
                    if (showTextColorPicker) {
                      handleTextColorChange(color);
                    } else {
                      handleBackgroundColorChange(color);
                    }
                  }}
                  sx={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: color,
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      border: '2px solid #1976d2',
                    },
                  }}
                />
              ))}
            </Box>
            
            {/* Custom Color Input */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ minWidth: '60px' }}>
                Custom:
              </Typography>
              <input
                type="color"
                value={showTextColorPicker ? textColor : backgroundColor}
                onChange={(e) => {
                  if (showTextColorPicker) {
                    handleTextColorChange(e.target.value);
                  } else {
                    handleBackgroundColorChange(e.target.value);
                  }
                }}
                style={{
                  width: '40px',
                  height: '32px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              />
              <TextField
                size="small"
                value={showTextColorPicker ? textColor : backgroundColor}
                onChange={(e) => {
                  if (showTextColorPicker) {
                    handleTextColorChange(e.target.value);
                  } else {
                    handleBackgroundColorChange(e.target.value);
                  }
                }}
                placeholder="#000000"
                sx={{ width: '100px' }}
              />
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button size="small" onClick={closeColorPickers}>
                Close
              </Button>
            </Box>
          </Box>
        </Popover>
        
        {/* Content Area */}
        <Box
          ref={containerRef}
          onClick={handleClick}
          sx={{
            flex: 1,
            width: '100%',
            minHeight: 0, // Allow flex shrinking
            overflow: 'hidden', // Hide parent scrollbar
            cursor: 'text',
            background: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center', // Center horizontally
            justifyContent: 'flex-start',
            // Clean editing styles for full content
            '& [contenteditable="true"]': {
              cursor: 'text !important',
              outline: 'none !important',
              minHeight: '400px !important',
              padding: '16px !important',
              border: '1px solid #e0e0e0 !important',
              borderRadius: '8px !important',
              backgroundColor: '#ffffff !important',
              fontFamily: 'inherit !important',
              fontSize: 'inherit !important',
              lineHeight: 'inherit !important',
              '&:focus': {
                borderColor: '#ccc !important',
                boxShadow: 'none !important',
              },
            },
            // Prevent hover effects on interactive elements
            '& a:hover, & button:hover, & input:hover, & select:hover, & textarea:hover': {
              backgroundColor: 'transparent !important',
              transform: 'none !important',
              boxShadow: 'none !important',
            },
          }}
        >
          <iframe
            id={isEditable ? "editable-html-container" : "preview-html-container"}
            style={{
              width: '100%',
              maxWidth: '800px', // Limit width for better centering
              height: '100vh', // Fixed reasonable height
              border: 'none',
              fontFamily: 'inherit',
              fontSize: 'inherit',
              lineHeight: 'inherit',
              backgroundColor: 'transparent',
              overflow: 'hidden',
            }}
            sandbox={isEditable ? "allow-scripts allow-same-origin" : "allow-same-origin"}
            title={isEditable ? "Email Editor" : "Email Preview"}
            ref={(iframe) => {
              if (iframe && !isIframeInitialized) {
                const initializeIframe = () => {
                  const doc = iframe.contentDocument;
                  if (doc) {
                    doc.open();
                    doc.write(`
                      <!DOCTYPE html>
                      <html>
                        <head>
                          <meta charset="utf-8">
                          <meta name="viewport" content="width=device-width, initial-scale=1">
                          <style>
                            html {
                              margin: 0;
                              padding: 0;
                              height: 100%;
                              overflow: hidden;
                            }
                            body {
                              margin: 0 auto;
                              padding: 16px;
                              font-family: inherit;
                              font-size: inherit;
                              line-height: inherit;
                              background: #ffffff;
                              height: 100%;
                              overflow-y: auto;
                              overflow-x: hidden;
                              max-width: 600px; /* Center email content */
                              ${isEditable ? `
                                border: 1px solid #e0e0e0;
                                border-radius: 8px;
                                outline: none;
                              ` : ''}
                            }
                            * {
                              box-sizing: border-box;
                            }
                            ${isEditable ? `
                              [contenteditable="true"] {
                                outline: none !important;
                                cursor: text !important;
                              }
                              [contenteditable="true"]:focus {
                                border-color: #ccc !important;
                                box-shadow: none !important;
                              }
                            ` : ''}
                          </style>
                        </head>
                        <body ${isEditable ? 'contenteditable="true"' : ''}>
                          ${htmlContent}
                        </body>
                      </html>
                    `);
                    doc.close();
                    
                    if (isEditable) {
                      // Setup event listeners only for editing mode
                      const body = doc.body;
                      editingElementRef.current = body;
                      
                      // Simple input handler without debouncing for immediate feedback
                      body.addEventListener('input', function(e) {
                        // Only send message if content actually changed
                        const currentContent = body.innerHTML;
                        window.parent.postMessage({
                          type: 'htmlChange',
                          html: currentContent
                        }, '*');
                      });
                      
                      // Handle paste events
                      body.addEventListener('paste', function(e) {
                        setTimeout(() => {
                          window.parent.postMessage({
                            type: 'htmlChange',
                            html: body.innerHTML
                          }, '*');
                        }, 10);
                      });
                      
                      // Focus the body
                      setTimeout(() => {
                        iframe.contentWindow?.focus();
                        body.focus();
                      }, 50);
                    }
                    
                    setIsIframeInitialized(true);
                  }
                };
                
                // Initialize immediately if document is ready, otherwise wait
                if (iframe.contentDocument?.readyState === 'complete') {
                  initializeIframe();
                } else {
                  iframe.onload = initializeIframe;
                }
              } else if (iframe && isIframeInitialized && isEditable) {
                // Only update content if it's different and we're not currently typing
                const body = iframe.contentDocument?.body;
                if (body && body.innerHTML !== htmlContent) {
                  // Check if user is actively typing by checking if the iframe is focused
                  const isActivelyEditing = iframe.contentDocument === document.activeElement?.ownerDocument;
                  
                  if (!isActivelyEditing) {
                    // Save current scroll position
                    const scrollTop = body.scrollTop;
                    const scrollLeft = body.scrollLeft;
                    
                    // Save cursor position
                    const selection = iframe.contentDocument?.getSelection();
                    let range: Range | null = null;
                    if (selection && selection.rangeCount > 0) {
                      range = selection.getRangeAt(0).cloneRange();
                    }
                    
                    // Update content
                    body.innerHTML = htmlContent;
                    
                    // Restore scroll position first
                    body.scrollTop = scrollTop;
                    body.scrollLeft = scrollLeft;
                    
                    // Restore cursor position
                    if (range && selection) {
                      try {
                        selection.removeAllRanges();
                        selection.addRange(range);
                      } catch (e) {
                        // If range is invalid, place cursor at end
                        const newRange = iframe.contentDocument!.createRange();
                        newRange.selectNodeContents(body);
                        newRange.collapse(false);
                        selection.removeAllRanges();
                        selection.addRange(newRange);
                      }
                    }
                  }
                }
                editingElementRef.current = body || null;
              }
            }}
          />
        </Box>
  
        {/* Enhanced editing indicator */}
      </Box>
    );
  };

  export default InPlaceEditableEmailPreviewMAIQ;